// owner-map: OP-10.spa
/**
 * Client side of the interaction-telemetry stream: ships ring-buffer entries
 * (lib/logRingBuffer.js) to `POST /api/telemetry/events`.
 *
 * - Started once in main.jsx, before React renders. Until the first /api/me
 *   answer (`enable(...)`, applied by hooks/useAuthSession.ts's fetchMe before it
 *   records anything for that answer) it only buffers; group events wait in
 *   lib/telemetry/groups.js until the answer says which groups are on.
 * - Enabled: sends every unacknowledged entry (`seq > last_acked_seq`, which
 *   covers entries from before a reload), then follows new pushes. Plain `fetch`
 *   every 5 s or at 50 pending entries (≤ 60 KB), one periodic request in flight.
 *   On hidden / pagehide, one final `keepalive` fetch (≤ 32 KB) from the oldest
 *   unacknowledged entry — it may overlap the periodic request; the server's
 *   deterministic event ids make a resend harmless.
 * - The watermark advances only through contiguous acknowledged ranges, so an
 *   earlier failure is resent rather than skipped.
 * - Disabled: every entry is acknowledged as it lands, so a later enable sends
 *   only entries created after it. Those entries, and the backlog acknowledged
 *   when the stream turns off, are counted; the next enable reports them as one
 *   `telemetry.dropped {reason: "pre_enable"}`, so a tab whose first streamed seq
 *   is above 1 says why.
 * - `tab_id` lives in sessionStorage; a duplicated tab copies it, so each start
 *   probes a BroadcastChannel and, when another live tab answers with the same
 *   id, takes a fresh id and leaves the copied backlog to the original tab.
 * - Failures are silent (no console output). A retryable failure (429, 401,
 *   5xx, network) keeps the range unacknowledged and backs off up to 60 s; a
 *   permanent rejection (400, 403, 413) acknowledges the range and reports it
 *   through telemetry.dropped, like a ring eviction.
 */

import {
  TELEMETRY_TAB_KEY as TAB_ID_KEY,
  ringBuffer,
  telemetryLog,
  traceRing,
} from "../lib/logRingBuffer";

export { TAB_ID_KEY };
import {
  KEEPALIVE_MAX_BYTES,
  PERIODIC_MAX_BYTES,
  PERIODIC_MAX_EVENTS,
  advanceWatermark,
  buildBatch,
  classifyStatus,
} from "../lib/telemetry/batch.js";
import { CLIENT_BUILD_SHA } from "../lib/telemetry/buildSha.js";
import {
  recordTraceEvent,
  releasePreAnswer,
  setTelemetryGroups,
  takeBudgetDrops,
  takePreAnswerDrops,
  telemetryGroupOn,
} from "../lib/telemetry/groups.js";
import { guarded } from "../lib/telemetry/guard.js";
import { createConsoleCapture } from "./consoleCapture.js";
import { createFrictionCapture } from "./frictionCapture.js";
import { createInteractionCapture } from "./interactionCapture.js";
import { createNetCapture } from "./netCapture.js";
import { createPerfMonitor } from "./perf.js";
import { createIntentTelemetry } from "./intentTelemetry.js";
import { p2Telemetry, telemetryEntrySource } from "./p2Telemetry.js";

export const TELEMETRY_ENDPOINT = "/api/telemetry/events";
export const CHANNEL_NAME = "sooth-telemetry";
const INTERVAL_MS = 5_000;
const PROBE_MS = 250;
const MAX_BACKOFF_MS = 60_000;
/** How many recently counted seqs the streamed counter remembers to skip redeliveries. */
const STREAMED_RECENT_MAX = 2048;
/** At most one telemetry.persist_failed report per this long (its own write may fail). */
const PERSIST_REPORT_MS = 60_000;

function newTabId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeStorage() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Create a telemetry client; the app uses the module singleton below. */
export function createTelemetry({
  buffer = telemetryLog,
  fetchImpl = (...args) => fetch(...args),
  win = typeof window !== "undefined" ? window : null,
  doc = typeof document !== "undefined" ? document : null,
  storage = safeStorage(),
  BroadcastChannelImpl = typeof BroadcastChannel !== "undefined" ? BroadcastChannel : null,
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
  setIntervalImpl = (fn, ms) => setInterval(fn, ms),
  clearIntervalImpl = (id) => clearInterval(id),
  now = () => Date.now(),
  perf = createPerfMonitor({ record: (e) => buffer.push(e) }),
  applyGroups = setTelemetryGroups,
  releaseQueued = releasePreAnswer,
  takeBudget = takeBudgetDrops,
  takePreAnswer = takePreAnswerDrops,
  interaction = createInteractionCapture({ record: recordTraceEvent }),
  net = createNetCapture({ record: recordTraceEvent, recording: () => telemetryGroupOn("net") }),
  consoleCapture = createConsoleCapture({
    record: recordTraceEvent,
    recording: () => telemetryGroupOn("diagnostics"),
  }),
  friction = createFrictionCapture({ record: recordTraceEvent }),
  buildSha = CLIENT_BUILD_SHA,
  intervalMs = INTERVAL_MS,
  probeMs = PROBE_MS,
  p2 = null,
  intents = null,
} = {}) {
  let started = false;
  /** null until the first /api/me answer, then the flag's value. */
  let enabled = null;
  let ready = false;
  /** Resolves once the duplicate-tab probe has settled (`ready`): a drain waits for it. */
  let markReady = () => {};
  const readyPromise = new Promise((resolve) => {
    markReady = resolve;
  });
  const setReady = () => {
    ready = true;
    markReady();
  };
  let tabId = null;
  let probeNonce = null;
  let channel = null;
  let timer = null;
  let inFlight = false;
  /** The periodic request in flight, so a drain can wait for it. */
  let periodicSend = null;
  let keepaliveInFlight = false;
  /** True while a batch is built: its telemetry.dropped push must not start a flush. */
  let building = false;
  /** Bumped when the tab id changes; a response for an older identity is ignored. */
  let generation = 0;
  /** Watermark when streaming was last enabled: entries through it are never streamed. */
  let enabledFloor = 0;
  let ackedRanges = [];
  /** Seqs a permanent rejection covered, pending the overlapping request's answer. */
  let rejected = [];
  // Events the server acknowledged (the account panel's count). An overlapping batch may
  // redeliver a seq, so the most recently counted seqs are remembered — a bounded set,
  // oldest dropped first, far larger than any overlap between two in-flight requests.
  let streamedCount = 0;
  const recentlyCounted = new Set();
  /** Seqs a successful request carried while another was in flight. */
  const delivered = new Set();
  /** Batches whose request has not settled yet. */
  const activeBatches = new Set();
  /**
   * Seqs an in-flight request carried when the stream turned off: delivered if it
   * succeeds, skipped if it must be retried (nothing resends them once off).
   */
  const undecidedAtDisable = new Set();
  /** Drop reports such a request carried, by seq: their losses are skipped if it is retried. */
  const undecidedReports = new Map();
  let backoffMs = 0;
  let persistReportedAt = -Infinity;
  let backoffUntil = 0;

  function saveTabId() {
    try {
      storage?.setItem(TAB_ID_KEY, tabId);
    } catch {
      /* best effort */
    }
  }

  function loadTabId() {
    try {
      const v = storage?.getItem(TAB_ID_KEY);
      if (v) return v;
    } catch {
      /* fall through */
    }
    return null;
  }

  function onChannelMessage(msg) {
    const data = msg && msg.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "probe" && data.tab_id === tabId && data.nonce !== probeNonce) {
      try {
        channel.postMessage({ type: "taken", tab_id: data.tab_id, nonce: data.nonce });
      } catch {
        /* best effort */
      }
    } else if (data.type === "taken" && data.nonce === probeNonce && data.tab_id === tabId) {
      // This tab is a duplicate: its sessionStorage (tab id, seq counters, backlog)
      // was copied from a live tab, which alone sends that backlog.
      // A late reply (after the probe timeout) can follow sends under the copied
      // id: entries this page pushed are resent under the new one.
      tabId = newTabId();
      saveTabId();
      generation += 1;
      if (enabled === true) buffer.rewindAck(Math.max(buffer.hydratedThroughSeq, enabledFloor));
      buffer.ack(buffer.hydratedThroughSeq);
      ackedRanges = [];
      rejected = [];
      delivered.clear();
    }
  }

  function recordAck(fromSeq, toSeq) {
    const { watermark, pending } = advanceWatermark(buffer.lastAckedSeq, [
      ...ackedRanges,
      [fromSeq, toSeq],
    ]);
    ackedRanges = pending;
    buffer.ack(watermark);
  }

  async function send(body, keepalive) {
    try {
      const r = await guarded(() =>
        fetchImpl(TELEMETRY_ENDPOINT, {
          method: "POST",
          headers: { "content-type": "application/json", "x-client-build-sha": buildSha },
          body,
          credentials: "same-origin",
          keepalive,
        }),
      );
      return classifyStatus(r ? r.status : 0);
    } catch {
      return "retry";
    }
  }

  /** Apply a send outcome to the watermark and the backoff. */
  function settle(batch, outcome, { backoff }) {
    // A batch sent under a tab id this page has since replaced acknowledges nothing.
    const current = batch.generation === generation;
    if (current && outcome === "permanent") {
      // An entry evicted while its batch was in flight was counted then.
      // Two overlapping rejections may carry the same seq: count it once.
      for (const s of batch.lossSeqs) {
        if (!delivered.has(s) && !buffer.isCounted(s) && !rejected.includes(s)) rejected.push(s);
      }
      recordAck(batch.fromSeq, batch.toSeq);
    } else if (current && outcome === "ok") {
      for (const s of batch.seqs) {
        if (recentlyCounted.has(s)) continue;
        streamedCount += 1;
        recentlyCounted.add(s);
        // A Set iterates in insertion order: the first key is the oldest.
        if (recentlyCounted.size > STREAMED_RECENT_MAX) {
          recentlyCounted.delete(recentlyCounted.values().next().value);
        }
      }
      for (const s of batch.lossSeqs) delivered.add(s);
      rejected = rejected.filter((s) => !delivered.has(s));
      recordAck(batch.fromSeq, batch.toSeq);
    }
    // A rejected seq is lost only if no overlapping request delivered it — by the
    // entries it actually carried, not its seq range, which spans gaps — so the count
    // waits until both have settled.
    // A permanent rejection is counted through `rejected` above; success is delivery.
    if (current && outcome !== "retry") {
      for (const s of batch.seqs) {
        undecidedAtDisable.delete(s);
        undecidedReports.delete(s);
      }
    }
    if (!inFlight && !keepaliveInFlight) {
      if (undecidedAtDisable.size > 0 || undecidedReports.size > 0) {
        if (undecidedAtDisable.size > 0) {
          buffer.noteSkipped?.(undecidedAtDisable.size, Math.max(...undecidedAtDisable));
        }
        for (const r of undecidedReports.values()) {
          buffer.noteSkipped?.(r.dropped_count, r.discarded_through_seq ?? 0);
        }
        undecidedAtDisable.clear();
        undecidedReports.clear();
        // Re-enabled before this request settled: report now, not at the next enable.
        if (enabled === true) reportPreEnable();
      }
      if (rejected.length > 0) buffer.recordDropped(rejected.length, Math.max(...rejected));
      rejected = [];
      delivered.clear();
      buffer.pruneCounted();
    }
    if (backoff) noteResult(outcome !== "retry");
  }

  function noteResult(ok) {
    if (ok) {
      backoffMs = 0;
      backoffUntil = 0;
    } else {
      backoffMs = Math.min(MAX_BACKOFF_MS, Math.max(intervalMs, backoffMs * 2));
      backoffUntil = now() + backoffMs;
    }
  }

  function nextBatch(maxEvents, maxBytes) {
    building = true;
    try {
      // While a report is still unsent its successor waits: pushing one per retry
      // would evict another entry from a full ring on every failed send.
      const pending = buffer.unacked();
      const reportPending = pending.some((e) => e.event === "telemetry.dropped");
      const dropped = reportPending ? null : buffer.takeDropped();
      const byKind = reportPending ? null : takeBudget();
      if (dropped || byKind) {
        const overBudget = byKind ? Object.values(byKind).reduce((a, b) => a + b, 0) : 0;
        buffer.push({
          event: "telemetry.dropped",
          dropped_count: (dropped ? dropped.count : 0) + overBudget,
          discarded_through_seq: dropped ? dropped.through : null,
          ...(byKind ? { by_kind: byKind } : {}),
        });
      }
      const t = now();
      if (t - persistReportedAt >= PERSIST_REPORT_MS) {
        const failed = buffer.takePersistFailed?.();
        if (failed) {
          persistReportedAt = t;
          for (const [ring, count] of Object.entries(failed)) {
            buffer.push({ event: "telemetry.persist_failed", count, ring });
          }
        }
      }
      const batch = buildBatch(buffer.unacked(), {
        tabId,
        maxEvents,
        maxBytes,
        fromSeq: buffer.lastAckedSeq + 1,
      });
      if (batch) batch.generation = generation;
      return batch;
    } finally {
      building = false;
    }
  }

  /**
   * Send one periodic batch if allowed; resolves when it completes. `ignoreBackoff`: a drain
   * before sign-out tries once more even while backing off.
   */
  async function flush({ ignoreBackoff = false } = {}) {
    if (enabled !== true || !ready || inFlight || building) return;
    if (!ignoreBackoff && now() < backoffUntil) return;
    let batch;
    try {
      batch = nextBatch(PERIODIC_MAX_EVENTS, PERIODIC_MAX_BYTES);
    } catch {
      return;
    }
    if (!batch) return;
    inFlight = true;
    activeBatches.add(batch);
    const sending = send(batch.body, false);
    periodicSend = sending;
    const outcome = await sending;
    periodicSend = null;
    activeBatches.delete(batch);
    inFlight = false;
    settle(batch, outcome, { backoff: true });
    if (outcome !== "retry" && enabled === true && buffer.unacked().length >= PERIODIC_MAX_EVENTS) {
      return flush();
    }
  }

  /**
   * Send everything still unacknowledged, now: waits for the periodic requests already in
   * flight (however many a backlog chains), then sends batches until nothing is left or a send
   * makes no progress. Used before a sign-out, whose session the next upload would no longer
   * have. Never rejects; the caller bounds the wait (drainTelemetry).
   */
  async function drain() {
    try {
      // A decline right after load can come before the duplicate-tab probe settles; its
      // events are sent once it has (under the tab id it settles on).
      if (!ready) await readyPromise;
      for (;;) {
        if (enabled !== true || buffer.unacked().length === 0) return;
        if (periodicSend) {
          // Not a pass of our own: a chained backlog may hold several in turn.
          await periodicSend;
          // flush() settles the batch on the same resolution; let it run first.
          await Promise.resolve();
          continue;
        }
        const before = buffer.lastAckedSeq;
        await flush({ ignoreBackoff: true });
        if (buffer.lastAckedSeq === before && !periodicSend) return;
      }
    } catch {
      /* telemetry never blocks a sign-out */
    }
  }

  /** Final keepalive send on hide / pagehide. */
  async function finalFlush() {
    if (enabled !== true || !ready || keepaliveInFlight || building) return;
    let batch;
    try {
      batch = nextBatch(Infinity, KEEPALIVE_MAX_BYTES);
    } catch {
      return;
    }
    if (!batch) return;
    keepaliveInFlight = true;
    activeBatches.add(batch);
    const outcome = await send(batch.body, true);
    activeBatches.delete(batch);
    keepaliveInFlight = false;
    settle(batch, outcome, { backoff: false });
  }

  /** One `telemetry.dropped {reason: "pre_enable"}` for what the stream never got to send. */
  function reportPreEnable() {
    const skipped = buffer.takeSkipped?.() ?? null;
    const early = takePreAnswer();
    if (!skipped && !early) return;
    const earlyCount = early ? Object.values(early).reduce((a, b) => a + b, 0) : 0;
    buffer.push({
      event: "telemetry.dropped",
      reason: "pre_enable",
      dropped_count: (skipped ? skipped.count : 0) + earlyCount,
      discarded_through_seq: skipped ? skipped.through : null,
      ...(early ? { by_kind: early } : {}),
    });
  }

  function onEntry() {
    if (
      enabled === true &&
      !inFlight &&
      !building &&
      buffer.unacked().length >= PERIODIC_MAX_EVENTS
    ) {
      flush();
    }
  }

  function onVisibility() {
    if (doc && doc.visibilityState === "hidden") finalFlush();
  }

  function onPageHide() {
    finalFlush();
  }

  function startProbe() {
    if (!BroadcastChannelImpl) {
      setReady();
      return;
    }
    try {
      channel = new BroadcastChannelImpl(CHANNEL_NAME);
      channel.onmessage = onChannelMessage;
      probeNonce = newTabId();
      channel.postMessage({ type: "probe", tab_id: tabId, nonce: probeNonce });
    } catch {
      setReady();
      return;
    }
    setTimeoutImpl(() => {
      setReady();
      flush();
    }, probeMs);
  }

  return {
    /** Wire listeners and the tab id; idempotent. */
    start() {
      if (started) return;
      started = true;
      try {
        tabId = loadTabId() || newTabId();
        saveTabId();
        buffer.subscribe(onEntry);
        doc?.addEventListener("visibilitychange", onVisibility);
        win?.addEventListener("pagehide", onPageHide);
        startProbe();
      } catch {
        /* telemetry must never break boot */
      }
    },
    /**
     * Apply the `telemetryStreamEnabled` flag and the sub-flag groups from /api/me
     * (lib/telemetry/groups.js). A group is on only while the stream is. `identity`
     * (`{user, org, flags}`, lib/telemetry/tabStart.js) scopes the P2 visits.
     */
    enable(on, groups = null, identity = null) {
      const next = on === true;
      try {
        // Whatever P2 turns off stops while its group is still on, so its final spans are
        // recorded; what it turns on starts after the groups are on.
        p2?.prepare(next ? groups : null);
        // Events queued and intents held before the first answer replay after P2 stamps the
        // visit, so they carry its visit_id (visits.ts stamps a provisional id at once, before
        // its Web Lock settles); they keep their capture times.
        applyGroups(next ? groups : null, { deferReplay: true });
        p2?.apply(next ? groups : null, next ? identity : null);
        releaseQueued();
        const on = (group) => next && groups?.[group] === true;
        intents?.apply(on("intent"), next ? identity : null);
        // The fetch patch and the console wrapper also run for the friction group, which
        // watches requests and errors through them without recording either.
        for (const [capture, running] of [
          [interaction, on("interaction")],
          [net, on("net") || on("friction")],
          [consoleCapture, on("diagnostics") || on("friction")],
          [friction, on("friction")],
        ]) {
          if (running) capture.start();
          else capture.stop();
        }
      } catch {
        /* never surface */
      }
      if (next === enabled) return;
      enabled = next;
      try {
        if (!next) {
          buffer.autoAck = true;
          // Already counted entries (evicted unsent) are not counted twice, and an
          // in-flight request's entries wait for its outcome (settle()); a carried
          // telemetry.dropped report is not a loss of its own (buildBatch's lossSeqs).
          // The losses it reports, though, wait with its request.
          const carried = new Set([...activeBatches].flatMap((b) => b.seqs));
          for (const b of activeBatches) {
            for (const s of b.lossSeqs) if (!buffer.isCounted(s)) undecidedAtDisable.add(s);
          }
          const pending = buffer.unacked();
          for (const e of pending) {
            if (carried.has(e.seq) && e.event === "telemetry.dropped" && e.dropped_count > 0) {
              undecidedReports.set(e.seq, e);
            }
          }
          const unsent = pending.filter((e) => !buffer.isCounted(e.seq) && !carried.has(e.seq));
          // An unsent drop report is not a loss itself; the losses it would have
          // reported move to the next pre_enable report instead.
          const losses = unsent.filter((e) => e.event !== "telemetry.dropped");
          if (losses.length > 0) {
            buffer.noteSkipped?.(losses.length, losses[losses.length - 1].seq);
          }
          for (const e of unsent) {
            if (e.event === "telemetry.dropped" && e.dropped_count > 0) {
              buffer.noteSkipped?.(e.dropped_count, e.discarded_through_seq ?? 0);
            }
          }
          buffer.ackAll();
          // Evictions not yet reported move to the next enable's pre_enable report,
          // which must explain the whole seq gap.
          const evicted = buffer.takeDropped();
          if (evicted) buffer.noteSkipped?.(evicted.count, evicted.through);
          ackedRanges = [];
          if (timer !== null) clearIntervalImpl(timer);
          timer = null;
          perf.stop();
          return;
        }
        buffer.autoAck = false;
        enabledFloor = buffer.lastAckedSeq;
        reportPreEnable();
        timer = setIntervalImpl(() => {
          flush();
        }, intervalMs);
        perf.start();
        flush();
      } catch {
        /* never surface */
      }
    },
    flush,
    finalFlush,
    drain,
    get tabId() {
      return tabId;
    },
    get enabled() {
      return enabled;
    },
    /** Events this page has had acknowledged by the server. */
    get streamedCount() {
      return streamedCount;
    },
  };
}

let singleton = null;

/** Start the app's telemetry client (main.jsx, before createRoot). */
export function startTelemetry() {
  if (!singleton) {
    singleton = createTelemetry({
      p2: p2Telemetry(),
      intents: createIntentTelemetry({
        getTabId: () => singleton?.tabId ?? null,
        getEntrySource: telemetryEntrySource,
      }),
    });
  }
  singleton.start();
  return singleton;
}

/**
 * Apply the /api/me flags (and the identity the P2 visits are scoped to) to the client.
 * @param {boolean} on
 * @param {object | null} [groups] `groupsFromMe(me)` (lib/telemetry/groups.js)
 * @param {object | null} [identity] `telemetryIdentityFromMe(me)` (lib/telemetry/tabStart.js)
 */
export function enableTelemetry(on, groups = null, identity = null) {
  singleton?.enable(on, groups, identity);
}

/** How long a sign-out waits for its telemetry before going ahead regardless. */
export const DRAIN_TIMEOUT_MS = 1_500;

/**
 * Upload what this tab has queued before the session ends (sign-out, a declined ToS): the
 * upload after logout would be refused. Resolves after at most `timeoutMs`, never rejects —
 * telemetry never holds up or breaks the sign-out.
 */
export function drainTelemetry({
  client = singleton,
  timeoutMs = DRAIN_TIMEOUT_MS,
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
} = {}) {
  if (!client?.drain) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => resolve(undefined);
    setTimeoutImpl(done, timeoutMs);
    Promise.resolve()
      .then(() => client.drain())
      .then(done, done);
  });
}

/** This tab's telemetry id, or null before startTelemetry(). */
export function telemetryTabId() {
  return singleton ? singleton.tabId : null;
}

/**
 * The account panel's log counts: entries in the ui (domain) ring and the trace ring,
 * and the events this page has streamed — null while the stream flag is off.
 */
export function telemetryLogCounts() {
  return {
    ui: ringBuffer.buffer.length,
    trace: traceRing.buffer.length,
    streamed: singleton?.enabled === true ? singleton.streamedCount : null,
  };
}
